"use client";

import TranslationFriendlyLink from "@/components/TranslationFriendlyLink";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import { translationToolDetected } from "@/state";

const navItems = [
    {
        label: "AWS",
        href: "/",
        children: [
            {
                label: "EC2",
                href: "/",
            },
            {
                label: "RDS",
                href: "/rds",
            },
            {
                label: "ElastiCache",
                href: "/cache",
            },
            {
                label: "Redshift",
                href: "/redshift",
            },
            {
                label: "OpenSearch",
                href: "/opensearch",
            },
        ],
    },
    {
        label: "Azure",
        href: "/azure",
    },
    {
        label: "GCP",
        href: "/gcp",
    },
    {
        label: "Hetzner",
        href: "/hetzner",
    },
];

function TranslationToolDetector({
    className,
    text,
}: {
    className: string;
    text: string;
}) {
    const spanRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        const span = spanRef.current;
        if (!span) return;

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                const textContent = mutation.target.textContent;
                if (textContent !== text) {
                    translationToolDetected.set(true);
                    observer.disconnect();
                }
            });
        });
        observer.observe(span, { childList: true });
        return () => observer.disconnect();
    }, [text, spanRef.current, translationToolDetected]);

    return (
        <span ref={spanRef} className={className}>
            {text}
        </span>
    );
}

type NavItemProps = {
    item: {
        label: string;
        href: string;
        children?: {
            label: string;
            href: string;
        }[];
    };
    currentPath: string;
};

function NavItem({ item, currentPath }: NavItemProps) {
    let anySelected = false;
    const children =
        item.children &&
        item.children.map((child) => {
            const selected =
                currentPath === child.href ||
                currentPath.includes(child.label.toLowerCase());
            if (selected) anySelected = true;
            return (
                <TranslationFriendlyLink
                    aria-current={selected}
                    className={`font-normal text-sm px-2 py-1 pb-2 rounded rounded-b-none ${
                        selected
                            ? "bg-white text-black font-semibold"
                            : "text-white"
                    }`}
                    key={child.label}
                    href={child.href}
                >
                    {child.label}
                </TranslationFriendlyLink>
            );
        });
    if (
        !anySelected &&
        currentPath.startsWith(item.href) &&
        item.href !== "/"
    ) {
        anySelected = true;
    }

    return (
        <div
            className="flex items-center justify-start gap-4 relative top-1.5 ml-2"
            key={item.label}
        >
            <TranslationFriendlyLink
                className="font-medium text-gray-4 text-sm"
                href={item.href}
                aria-current={anySelected}
            >
                <span
                    className={
                        anySelected ? "font-bold text-white" : "text-white"
                    }
                >
                    {item.label}
                </span>
            </TranslationFriendlyLink>
            {children && (
                <div className="flex items-center justify-start gap-4 rounded-md rounded-b-none bg-black/30 not-lg:hidden p-1 pb-0">
                    {children}
                </div>
            )}
        </div>
    );
}

export default function TopNav() {
    const currentPath = usePathname();

    return (
        <nav className="flex items-center justify-between bg-purple-brand h-[3rem] py-2 px-4 dark:bg-purple-dark">
            <div className="flex items-center justify-start gap-4">
                <TranslationFriendlyLink
                    href="/"
                    className="font-medium text-gray-4"
                >
                    <div className="flex items-center justify-start gap-2">
                        {/* The ">" mark from netservice.dev (its favicon), in white for the nav bar. */}
                        <svg
                            width="28"
                            height="28"
                            viewBox="0 0 1024 1024"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                        >
                            <polygon
                                fill="white"
                                points="275 777.5 743.69 517.68 743.69 506.32 275 247.92 275 96 883.87 438.17 883.87 585.83 275 928"
                            />
                        </svg>
                        <div className="flex flex-col">
                            <span className="font-semibold text-white leading-5">
                                Instances
                            </span>
                            <TranslationToolDetector
                                className="text-xs italic text-white"
                                text="by Net service"
                            />
                        </div>
                    </div>
                </TranslationFriendlyLink>
                {navItems.map((item) => (
                    <NavItem
                        key={item.label}
                        item={item}
                        currentPath={currentPath}
                    />
                ))}
            </div>
        </nav>
    );
}
